import React, { useContext, useEffect, useState, useRef, useMemo } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import ListItemButton from "@mui/material/ListItemButton";
import Typography from "@mui/material/Typography";
import Collapse from "@mui/material/Collapse";
import Tooltip from "@mui/material/Tooltip";
import InputBase from "@mui/material/InputBase";
import IconButton from "@mui/material/IconButton";
import Avatar from "@mui/material/Avatar";
import Divider from "@mui/material/Divider";
import Portal from "@mui/material/Portal";

import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import HeadsetMicIcon from "@mui/icons-material/HeadsetMic";
import BarChartIcon from "@mui/icons-material/BarChart";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";

import isotipo from "../assets/isotipo-shessai.png";

import { AuthContext } from "../context/Auth/AuthContext";
import { useActiveMenu } from "../context/ActiveMenuContext";
import useVersion from "../hooks/useVersion";
import useNavGroups from "../hooks/useNavGroups";

const BADGE = {
  blue:  { bg: "rgba(3,153,255,0.20)",  text: "#60A5FA" },
  red:   { bg: "rgba(239,68,68,0.20)",  text: "#F87171" },
  green: { bg: "rgba(34,197,94,0.20)",  text: "#4ADE80" },
  amber: { bg: "rgba(245,158,11,0.20)", text: "#FBBF24" },
};

// Exportado (ademas de usarse aca) para que layout/index.js (menu "Mas" de
// mobile) use el mismo mapa -- una sola fuente de verdad para el icono de
// cada categoria, en vez de que cada superficie invente el suyo.
export const CATEGORY_ICONS = {
  atencion:       <HeadsetMicIcon />,
  analisis:       <BarChartIcon />,
  automatizacion: <BoltOutlinedIcon />,
  admin:          <ManageAccountsOutlinedIcon />,
  sistema:        <SettingsOutlinedIcon />,
};

// ── NavItem (expanded mode) ─────────────────────────────────────
function NavItem({ item, drawerOpen, location }) {
  const theme = useTheme();
  const isActive = location.pathname === item.to ||
    (item.to !== "/" && location.pathname.startsWith(item.to));

  const renderLink = useMemo(
    () => React.forwardRef((p, ref) => <RouterLink to={item.to} ref={ref} {...p} />),
    [item.to]
  );

  const inner = (
    <ListItemButton component={renderLink} sx={{
      py: "5px", px: "8px", borderRadius: "8px", minHeight: 33, gap: "9px",
      mx: "4px", width: "calc(100% - 8px)", position: "relative",
      transition: "background 0.12s ease",
      background: isActive ? "rgba(3,153,255,0.09)" : "transparent",
      "&::before": isActive ? {
        content: '""', position: "absolute", left: 0, top: "50%",
        transform: "translateY(-50%)", width: "2.5px", height: "18px",
        background: "#0399FF", borderRadius: "0 2px 2px 0",
      } : {},
      "&:hover": { background: isActive ? "rgba(3,153,255,0.11)" : (theme.palette.mode==="dark"?"rgba(255,255,255,0.05)":"rgba(0,0,0,0.05)") },
    }}>
      <Box sx={{
        position: "relative",
        color: isActive ? "#0399FF" : (item.accent || "rgba(255,255,255,0.40)"),
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 16, width: 17, flexShrink: 0, transition: "color 0.12s",
        "& svg": { fontSize: "1.05rem" },
      }}>
        {item.icon}
        {item.dot && (
          <Box sx={{
            position: "absolute", top: -2, right: -3,
            width: 6, height: 6, borderRadius: "50%",
            background: "#0399FF",
          }} />
        )}
        {!drawerOpen && item.nuevo && (
          <Box sx={{
            position: "absolute", top: -2, right: -3,
            width: 6, height: 6, borderRadius: "50%",
            background: "#a78bfa",
          }} />
        )}
      </Box>
      {drawerOpen && (
        <>
          <Typography sx={{
            color: isActive ? "rgba(255,255,255,0.88)" : "rgba(255,255,255,0.55)",
            fontSize: 12.5, flex: 1, fontWeight: isActive ? 500 : 400,
            lineHeight: 1.2, transition: "color 0.12s",
          }}>
            {item.label}
          </Typography>
          {item.nuevo && (
            <Box sx={{
              fontSize: 10, fontWeight: 700, px: "7px", py: "2px",
              borderRadius: "999px", background: "#2a1f4a", color: "#cbbcff",
              flexShrink: 0, lineHeight: 1.3,
            }}>
              NUEVO
            </Box>
          )}
          {item.badge > 0 && item.badgeColor && (
            <Box sx={{
              background: BADGE[item.badgeColor].bg, color: BADGE[item.badgeColor].text,
              fontSize: 9, px: "5px", py: "1px", borderRadius: "9px",
              fontWeight: 700, lineHeight: 1.5, flexShrink: 0,
            }}>
              {item.badge}
            </Box>
          )}
        </>
      )}
    </ListItemButton>
  );

  if (!drawerOpen) {
    return <Tooltip title={item.label} placement="right" arrow disableInteractive>{inner}</Tooltip>;
  }
  return inner;
}

// ── RailQuickItem (acceso rapido destacado -- "Tu dia"/"Shessai IA",
// riel/asomar: mismo trato visual que una categoria de un solo item,
// pero fuera de cualquier categoria) ────────────────────────────
function RailQuickItem({ item, location, theme }) {
  const isActive = location.pathname === item.to ||
    (item.to !== "/" && location.pathname.startsWith(item.to));
  const iconColor = isActive ? "#0399FF" : (item.accent || "rgba(255,255,255,0.40)");
  return (
    <Tooltip title={item.label} placement="right" arrow disableInteractive>
      <Box component={RouterLink} to={item.to} sx={{
        width: 38, height: 38, borderRadius: "10px",
        mx: "auto", mb: "3px", textDecoration: "none",
        display: "flex", alignItems: "center", justifyContent: "center",
        position: "relative",
        background: isActive ? "rgba(3,153,255,0.15)" : "transparent",
        color: iconColor,
        "& svg": { fontSize: "1.35rem" },
        transition: "background 0.12s, color 0.12s",
        "&:hover": { background: (theme.palette.mode==="dark"?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.06)") },
        ...(isActive && {
          "&::before": {
            content: '""', position: "absolute",
            left: 0, top: "50%", transform: "translateY(-50%)",
            width: "3px", height: "22px",
            background: "#0399FF", borderRadius: "0 3px 3px 0",
          },
        }),
      }}>
        {item.icon}
        {item.dot && (
          <Box sx={{
            position: "absolute", top: 4, right: 6,
            width: 6, height: 6, borderRadius: "50%",
            background: "#0399FF",
          }} />
        )}
      </Box>
    </Tooltip>
  );
}

// ── NavGroup (expanded mode) ────────────────────────────────────
function NavGroup({ group, drawerOpen, isOpen, onToggle, location }) {
  const theme = useTheme();
  if (!drawerOpen) {
    return (
      <>
        {group.items.filter(i => !i.hidden).map(item => (
          <NavItem key={item.to} item={item} drawerOpen={false} location={location} />
        ))}
        <Divider sx={{ mx: "8px", my: 0.6, opacity: 0.10 }} />
      </>
    );
  }
  return (
    <>
      <Box onClick={onToggle} sx={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        px: "12px", py: "5px", mt: "6px", cursor: "pointer",
        "&:hover .grp-chevron": { opacity: 0.7 },
      }}>
        <Typography sx={{
          color: "rgba(255,255,255,0.55)", fontSize: 10, fontWeight: 600,
          letterSpacing: "0.08em", textTransform: "uppercase",
        }}>
          {group.label}
        </Typography>
        <Box className="grp-chevron" sx={{ color: (theme.palette.mode==="dark"?"rgba(255,255,255,0.55)":"rgba(0,0,0,0.55)"), opacity: 0.7, display: "flex", transition: "opacity 0.12s" }}>
          {isOpen ? <ExpandLessIcon sx={{ fontSize: 15 }} /> : <ExpandMoreIcon sx={{ fontSize: 15 }} />}
        </Box>
      </Box>
      <Collapse in={isOpen} timeout={150} unmountOnExit>
        {group.items.filter(i => !i.hidden).map(item => (
          <NavItem key={item.to} item={item} drawerOpen={true} location={location} />
        ))}
      </Collapse>
    </>
  );
}

// ── SubIconBtn (collapsed sub-column) ──────────────────────────
function SubIconBtn({ item, location, onNavigate }) {
  const theme = useTheme();
  const isActive = location.pathname === item.to ||
    (item.to !== "/" && location.pathname.startsWith(item.to));

  const renderLink = useMemo(
    () => React.forwardRef((p, ref) => <RouterLink to={item.to} ref={ref} {...p} />),
    [item.to]
  );

  return (
    <Tooltip title={item.label} placement="right" arrow disableInteractive>
      <ListItemButton component={renderLink} onClick={onNavigate} sx={{
        position: "relative",
        width: 84, minHeight: 52, borderRadius: "10px", p: "6px 4px",
        minWidth: 0, mx: "auto", mb: "2px",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "4px",
        background: isActive ? "rgba(3,153,255,0.12)" : "transparent",
        color: isActive ? "#0399FF" : "rgba(255,255,255,0.40)",
        transition: "background 0.12s, color 0.12s",
        "& svg": { fontSize: "1.15rem" },
        "&:hover": { background: (theme.palette.mode==="dark"?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.06)"), color: (theme.palette.mode==="dark"?"rgba(255,255,255,0.70)":"rgba(0,0,0,0.70)") },
      }}>
        {item.icon}
        <Typography sx={{ fontSize: 10.5, lineHeight: 1.2, textAlign: "center", color: "inherit" }}>
          {item.label}
        </Typography>
        {item.nuevo && (
          <Box sx={{
            position: "absolute", top: 6, right: 14,
            width: 7, height: 7, borderRadius: "50%",
            background: "#a78bfa",
          }} />
        )}
      </ListItemButton>
    </Tooltip>
  );
}

// ── Avatar footer (shared) ──────────────────────────────────────
function AvatarFooter({ userName, userInitials, drawerOpen, onProfileClick, userProfile }) {
  const theme = useTheme();
  return (
    <Box sx={{ p: "6px 7px", borderTop: `0.5px solid ${theme.palette.mode==="dark"?"rgba(255,255,255,0.05)":"rgba(0,0,0,0.05)"}`, flexShrink: 0 }}>
      <Box onClick={onProfileClick} sx={{
        display: "flex", alignItems: "center", gap: "8px",
        p: drawerOpen ? "5px 6px" : "5px 3px",
        borderRadius: "8px", cursor: "pointer",
        justifyContent: drawerOpen ? "flex-start" : "center",
        "&:hover": { background: (theme.palette.mode==="dark"?"rgba(255,255,255,0.05)":"rgba(0,0,0,0.05)") },
        transition: "background 0.12s",
      }}>
        <Box sx={{ position: "relative", flexShrink: 0 }}>
          <Avatar sx={{
            width: 36, height: 36, fontSize: 12, fontWeight: 700,
            background: "linear-gradient(135deg,#1e3a5f,#0d2a4a)",
            border: "1.5px solid rgba(96,165,250,0.25)",
          }}>
            {userInitials}
          </Avatar>
          <Box sx={{
            width: 8, height: 8, borderRadius: "50%", background: "#22c55e",
            position: "absolute", bottom: 0, right: 0,
            border: "1.5px solid rgba(13,22,40,0.98)",
          }} />
        </Box>
        {drawerOpen && (
          <>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ color: "rgba(255,255,255,0.80)", fontSize: 11.5, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {userName}
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: 10 }}>
                {userProfile === "admin" ? "Admin" : "Agente"} · En línea
              </Typography>
            </Box>
            <MoreVertIcon sx={{ color: "rgba(255,255,255,0.40)", fontSize: 14, flexShrink: 0 }} />
          </>
        )}
      </Box>
    </Box>
  );
}

// ── MainListItems ────────────────────────────────────────────────
const MainListItems = ({ drawerOpen, searchQuery, setSearchQuery, onProfileClick, onCatChange, onToggleDrawerOpen, onPeekMouseEnter, onPeekMouseLeave }) => {
  const theme = useTheme();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const { setActiveMenu } = useActiveMenu();
  const searchRef = useRef(null);

  // RDS-NAVGROUPS: feature flags, chats/badge, isAdmin y navGroups ahora viven
  // en el hook compartido useNavGroups (unico origen de datos junto al menu
  // movil "Mas" de layout/index.js).
  const { navGroups, quickAccess } = useNavGroups();
  const [version, setVersion] = useState("");

  // Expanded groups state
  const [openGroups, setOpenGroups] = useState({
    atencion: true, analisis: true, automatizacion: true, admin: false, sistema: false,
  });

  // Collapsed rail: which category sub-column is open
  const [activeCat, setActiveCat] = useState(null);
  // Fijado por CLIC (pedido explicito del usuario, 2026-10-23): un
  // panel abierto por hover se cierra solo (hover-intent); uno abierto
  // -o confirmado- por clic queda fijo y SOLO se cierra con clic fuera,
  // Esc, o clic de nuevo en el mismo icono -- nunca por mover el mouse.
  const [catPinned, setCatPinned] = useState(false);

  // Hover-intent del panel de subcategorias (riel colapsado, fijo o
  // flotando via asomar -- igual logica en los dos casos, este
  // componente no distingue entre ellos) -- 80ms para abrir, 400ms de
  // margen para cerrar (subido de 250ms, pedido explicito del
  // usuario), y el cierre por tiempo SOLO aplica si el panel no esta
  // fijado por clic. Independiente del navPeek de layout/index.js (ese
  // decide si el panel ENTERO esta flotando o no); este decide si HAY
  // una subcategoria abierta dentro de ese panel. onPeekMouseEnter/
  // onPeekMouseLeave (props, no-op si no se pasan -- ej. en movil)
  // cancelan/reinician el cierre del panel ENTERO mientras se
  // interactua con una categoria o su flyout, porque el flyout es
  // position:fixed y pinta FUERA del ancho del riel (52px a 148px,
  // ancho subido de 80 a 96 para caber el icono+etiqueta, punto 8):
  // sin este aviso, el navPeek de afuera (que solo ve su propio Box de
  // 52px) cree que el cursor ya salio y cierra todo.
  const catOpenTimerRef = useRef(null);
  const catCloseTimerRef = useRef(null);
  const clearCatTimers = () => {
    if (catOpenTimerRef.current) clearTimeout(catOpenTimerRef.current);
    if (catCloseTimerRef.current) clearTimeout(catCloseTimerRef.current);
    catOpenTimerRef.current = null;
    catCloseTimerRef.current = null;
  };
  const handleCatMouseEnter = (catId) => {
    onPeekMouseEnter?.();
    clearCatTimers();
    if (activeCat === catId) return;
    if (activeCat) {
      // Ya habia una categoria abierta -- moverse a otra la cambia sin
      // demora (nunca se cierra primero y abre despues). Una categoria
      // nueva por hover nunca llega fijada.
      setActiveCat(catId);
      setCatPinned(false);
    } else {
      catOpenTimerRef.current = setTimeout(() => { setActiveCat(catId); setCatPinned(false); }, 80);
    }
  };
  const handleCatMouseLeave = () => {
    if (catOpenTimerRef.current) { clearTimeout(catOpenTimerRef.current); catOpenTimerRef.current = null; }
    // Fijado por clic -- el mouse saliendo nunca lo cierra, ni por
    // dentro (catCloseTimerRef) ni por fuera (onPeekMouseLeave): en
    // modo asomar, avisar "el mouse salio" al navPeek EXTERIOR
    // (layout/index.js) igual dispara su propio cierre a los 250ms
    // (useHoverIntent, closeDelay default) aunque el panel de aca siga
    // fijado -- HALLAZGO REAL (probado con Playwright moviendo el mouse
    // del icono al flyout tras fijarlo con clic): sin esta guarda, un
    // panel fijado en modo asomar se cerraba solo de todos modos,
    // porque el riel ENTERO (no solo el flyout) depende de
    // navPeek.active ahi.
    if (!catPinned) {
      catCloseTimerRef.current = setTimeout(() => setActiveCat(null), 400);
      onPeekMouseLeave?.();
    }
  };
  const handleFlyoutMouseEnter = () => {
    onPeekMouseEnter?.();
    if (catCloseTimerRef.current) { clearTimeout(catCloseTimerRef.current); catCloseTimerRef.current = null; }
  };
  // Clic en el icono de una categoria: abre Y fija -- un clic
  // posterior en el MISMO icono, mientras ya esta fijado, es lo unico
  // que lo cierra desde aca (ademas de clic fuera y Esc, ver abajo).
  // onPeekMouseEnter() al fijar: en modo asomar, garantiza que el
  // riel EXTERIOR tambien quede "avisado" de inmediato (nunca
  // depender solo de que un mouseenter previo lo haya hecho).
  const handleCatClick = (catId) => {
    clearCatTimers();
    if (activeCat === catId && catPinned) {
      setActiveCat(null);
      setCatPinned(false);
      // Se suprimio onPeekMouseLeave mientras estaba fijado (ver
      // handleCatMouseLeave) -- al des-fijar aca hay que retomar el
      // reloj normal del riel exterior, o se queda abierto de mas en
      // modo asomar.
      onPeekMouseLeave?.();
    } else {
      onPeekMouseEnter?.();
      setActiveCat(catId);
      setCatPinned(true);
    }
  };
  useEffect(() => clearCatTimers, []);
  useEffect(() => {
    if (!activeCat) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") { clearCatTimers(); setActiveCat(null); setCatPinned(false); onPeekMouseLeave?.(); }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [activeCat]);

  const { getVersion } = useVersion();

  const toggleGroup = (id) => setOpenGroups(prev => ({ ...prev, [id]: !prev[id] }));

  useEffect(() => {
    if (location.pathname.startsWith("/tickets")) setActiveMenu("/tickets");
    else setActiveMenu("");
  }, [location, setActiveMenu]);

  useEffect(() => { getVersion().then(v => setVersion(v.version)); }, []);

  // ⌘K
  useEffect(() => {
    const h = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setTimeout(() => searchRef.current?.focus(), 50);
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);


  const filteredItems = searchQuery
    ? navGroups.flatMap(g =>
        g.items.filter(i => i.label?.toLowerCase().includes(searchQuery.toLowerCase()))
          .map(i => ({ ...i, _groupLabel: g.label }))
      )
    : null;

  const userName = user?.name || user?.email || "Usuario";
  const userInitials = userName.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();

  // ── Which category contains the active route (for collapsed mode) ──
  const activeCatForRoute = useMemo(() => {
    for (const g of navGroups) {
      if (g.items.some(i => i.to === location.pathname || (i.to !== "/" && location.pathname.startsWith(i.to)))) {
        return g.id;
      }
    }
    return null;
  }, [navGroups, location.pathname]);

  // Close sub-column when drawer opens
  useEffect(() => {
    if (drawerOpen && activeCat) {
      // Sync: open the matching group in expanded mode
      setOpenGroups(prev => ({ ...prev, [activeCat]: true }));
      setActiveCat(null);
      setCatPinned(false);
    }
  }, [drawerOpen]);
  // Reporta tambien si el panel abierto esta FIJADO -- layout/index.js
  // lo necesita para que su propio Box exterior (ancho fijo de
  // drawerWidth=52px, SIN contar el flyout de 52-148px) no cierre el
  // riel ENTERO en modo asomar en cuanto el mouse cruza esos 52px
  // hacia el flyout, aunque el panel siga fijado por clic. HALLAZGO
  // REAL (probado con Playwright): ese Box tiene su propio
  // onMouseLeave={navPeek.onMouseLeave} directo, ajeno por completo a
  // catPinned -- sin este aviso hacia arriba, fijar un panel en modo
  // asomar y mover el mouse hacia el igual lo cerraba.
  useEffect(() => { onCatChange?.(activeCat, catPinned); }, [activeCat, catPinned]);

  // Close flyout when clicking outside sidebar in collapsed mode --
  // cierra SIEMPRE, este fijado o no (pedido explicito del usuario).
  useEffect(() => {
    if (drawerOpen) return;
    const handler = (e) => {
      if (!e.target.closest || e.clientX > 148) { setActiveCat(null); setCatPinned(false); onPeekMouseLeave?.(); }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [drawerOpen]);

  // ── COLLAPSED RENDER ─────────────────────────────────────────────
  if (!drawerOpen) {
    const activeGroup = navGroups.find(g => g.id === activeCat);
    const isCatActive = (catId) => catId === activeCatForRoute;

    return (
      <Box sx={{ display: "flex", height: "100%", position: "relative" }}>
        {/* Primary rail — 52px */}
        <Box sx={{ width: 52, display: "flex", flexDirection: "column", height: "100%", flexShrink: 0 }}>
          {/* Logo */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 42, flexShrink: 0, borderBottom: `0.5px solid ${theme.palette.mode==="dark"?"rgba(255,255,255,0.04)":"rgba(0,0,0,0.04)"}` }}>
            <Box component="img" src={isotipo} alt="Shessai" sx={{ height: 28, width: 28, objectFit: "contain" }} />
          </Box>

          {/* Category buttons */}
          <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", pt: "4px", pb: "4px", scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}>
            {/* Acceso rapido destacado ("Tu dia"/"Shessai IA") -- antes de
                cualquier categoria, nunca duplicado dentro de Atencion. */}
            {quickAccess.length > 0 && (
              <>
                {quickAccess.map(item => (
                  <RailQuickItem key={item.to} item={item} location={location} theme={theme} />
                ))}
                <Divider sx={{ mx: "10px", my: "4px", opacity: 0.12 }} />
              </>
            )}
            {navGroups.filter(g => g.items.length > 0).map(g => {
              const isOpen = activeCat === g.id;
              const hasActive = isCatActive(g.id);

              // Grupos con un solo ítem navegan directo, sin flyout intermedio
              if (g.items.length === 1) {
                const item = g.items[0];
                const isActive = location.pathname === item.to ||
                  (item.to !== "/" && location.pathname.startsWith(item.to));
                return (
                  <Tooltip key={g.id} title={item.label} placement="right" arrow disableInteractive>
                    <Box component={RouterLink} to={item.to} sx={{
                      width: 38, height: 38, borderRadius: "10px",
                      mx: "auto", mb: "3px", textDecoration: "none",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      position: "relative",
                      background: isActive ? "rgba(3,153,255,0.15)" : "transparent",
                      color: isActive ? "#0399FF" : "rgba(255,255,255,0.40)",
                      "& svg": { fontSize: "1.35rem" },
                      transition: "background 0.12s, color 0.12s",
                      "&:hover": { background: (theme.palette.mode==="dark"?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.06)"), color: (theme.palette.mode==="dark"?"rgba(255,255,255,0.65)":"rgba(0,0,0,0.65)") },
                      ...(isActive && {
                        "&::before": {
                          content: '""', position: "absolute",
                          left: 0, top: "50%", transform: "translateY(-50%)",
                          width: "3px", height: "22px",
                          background: "#0399FF", borderRadius: "0 3px 3px 0",
                        },
                      }),
                    }}>
                      {CATEGORY_ICONS[g.id]}
                    </Box>
                  </Tooltip>
                );
              }

              return (
                <Tooltip key={g.id} title={g.label} placement="right" arrow disableInteractive>
                  <Box
                    onClick={() => handleCatClick(g.id)}
                    onMouseEnter={() => handleCatMouseEnter(g.id)}
                    onMouseLeave={handleCatMouseLeave}
                    sx={{
                    width: 38, height: 38, borderRadius: "10px",
                    mx: "auto", mb: "3px", cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    position: "relative",
                    background: isOpen ? "rgba(3,153,255,0.15)" : "transparent",
                    color: isOpen || hasActive ? "#0399FF" : "rgba(255,255,255,0.40)",
                    "& svg": { fontSize: "1.35rem" },
                    transition: "background 0.12s, color 0.12s",
                    "&:hover": { background: (theme.palette.mode==="dark"?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.06)"), color: (theme.palette.mode==="dark"?"rgba(255,255,255,0.65)":"rgba(0,0,0,0.65)") },
                    ...(isOpen && {
                      "&::before": {
                        content: '""', position: "absolute",
                        left: 0, top: "50%", transform: "translateY(-50%)",
                        width: "3px", height: "22px",
                        background: "#0399FF", borderRadius: "0 3px 3px 0",
                      },
                    }),
                  }}>
                    {CATEGORY_ICONS[g.id]}
                  </Box>
                </Tooltip>
              );
            })}
          </Box>

          {/* FASE-ENFOQUE: el control que antes vivia en el boton del
              header (hoy ese boton controla Enfoque/Fijo, ver
              layout/index.js) -- se mueve aca, abajo del riel. */}
          <Tooltip title="Expandir menú" placement="right" arrow disableInteractive>
            <IconButton
              size="small"
              onClick={onToggleDrawerOpen}
              aria-label="Expandir menú"
              sx={{
                width: 28, height: 28, borderRadius: "8px", mx: "auto", mb: "6px",
                color: "rgba(255,255,255,0.35)",
                "&:hover": { color: (theme.palette.mode==="dark"?"rgba(255,255,255,0.75)":"rgba(0,0,0,0.75)"), background: (theme.palette.mode==="dark"?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.06)") },
              }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Avatar always at bottom */}
          <AvatarFooter
            userName={userName}
            userInitials={userInitials}
            drawerOpen={false}
            onProfileClick={onProfileClick}
            userProfile={user.profile}
          />
        </Box>

        {/* Secondary flyout — renderizado en un Portal a document.body.
            HALLAZGO REAL (reportado por el usuario en Chrome real, no
            visible en las pruebas automaticas previas): el Box
            exterior del riel (layout/index.js) tiene `transform`
            (translateX, incluso "translateX(0)") ademas de
            `overflow:hidden` -- por spec CSS, CUALQUIER transform
            distinto de "none" en un ancestro convierte a ESE ancestro
            en el "containing block" de los descendientes
            position:fixed, asi que este Box NO se posicionaba contra
            el viewport pese a decir position:fixed; se posicionaba
            dentro del Box de 52px de ancho con overflow:hidden del
            riel, y quedaba recortado -- invisible, aunque
            getComputedStyle/getBoundingClientRect lo reportaran como
            si estuviera bien (esos metodos no reflejan el recorte por
            overflow de un ancestro). El Portal lo saca por completo
            del arbol del riel, asi position:fixed vuelve a ser
            relativo al viewport de verdad. */}
        <Portal>
          <Box
            onMouseEnter={handleFlyoutMouseEnter}
            onMouseLeave={handleCatMouseLeave}
            sx={{
            position: "fixed",
            left: 52, top: 0,
            width: activeCat ? 96 : 0,
            height: "100vh",
            overflow: "hidden",
            transition: "width 0.2s cubic-bezier(0.4,0,0.2,1)",
            background: "rgba(17,24,39,0.98)",

            borderRight: `0.5px solid ${theme.palette.mode==="dark"?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.08)"}`,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            zIndex: theme.zIndex.drawer + 20,
          }}>
            {activeCat && activeGroup && (
              <Box sx={{ width: 96, pt: "10px", pb: "4px", display: "flex", flexDirection: "column", alignItems: "center" }}>
                <Typography sx={{
                  color: "rgba(255,255,255,0.55)", fontSize: 9, fontWeight: 700,
                  letterSpacing: "0.08em", textTransform: "uppercase",
                  textAlign: "center", px: "4px", mb: "8px",
                  whiteSpace: "nowrap",
                }}>
                  {activeGroup.label}
                </Typography>
                {activeGroup.items.filter(i => !i.hidden).map(item => (
                  <SubIconBtn key={item.to} item={item} location={location} onNavigate={() => { clearCatTimers(); setActiveCat(null); setCatPinned(false); onPeekMouseLeave?.(); }} />
                ))}
              </Box>
            )}
          </Box>
        </Portal>
      </Box>
    );
  }

  // ── EXPANDED RENDER ──────────────────────────────────────────────
  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* Search bar */}
      <Box sx={{ px: "8px", pt: "6px", pb: "4px", flexShrink: 0, display: "flex", alignItems: "center", gap: "6px" }}>
        <Box sx={{
          flex: 1,
          display: "flex", alignItems: "center", gap: "6px",
          background: (theme.palette.mode==="dark"?"rgba(255,255,255,0.04)":"rgba(0,0,0,0.04)"),
          border: `0.5px solid ${searchQuery ? "rgba(3,153,255,0.40)" : "rgba(255,255,255,0.40)"}`,
          borderRadius: "8px", px: "9px", py: "5px",
          transition: "border-color 0.15s",
        }}>
          <SearchIcon sx={{ color: searchQuery ? "#0399FF" : "rgba(255,255,255,0.40)", fontSize: 13, flexShrink: 0 }} />
          <InputBase
            inputRef={searchRef}
            placeholder="Buscar sección..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            sx={{ flex: 1, fontSize: 12, color: "rgba(255,255,255,0.80)", "& input::placeholder": { color: "rgba(255,255,255,0.55)" } }}
          />
          {searchQuery ? (
            <IconButton size="small" onClick={() => setSearchQuery("")} sx={{ p: 0 }}>
              <CloseIcon sx={{ fontSize: 11, color: "rgba(255,255,255,0.40)" }} />
            </IconButton>
          ) : (
            <Box sx={{ display: "flex", gap: "2px", flexShrink: 0 }}>
              {["⌘","K"].map(k => (
                <Box key={k} sx={{ background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.55)", fontSize: 9, px: "4px", py: "1px", borderRadius: "3px", fontFamily: "monospace" }}>
                  {k}
                </Box>
              ))}
            </Box>
          )}
        </Box>
        <Tooltip title="Contraer menú" placement="right" arrow disableInteractive>
          <IconButton
            size="small"
            onClick={onToggleDrawerOpen}
            aria-label="Contraer menú"
            sx={{
              width: 26, height: 26, borderRadius: "7px", flexShrink: 0,
              color: "rgba(255,255,255,0.35)",
              "&:hover": { color: (theme.palette.mode==="dark"?"rgba(255,255,255,0.75)":"rgba(0,0,0,0.75)"), background: (theme.palette.mode==="dark"?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.06)") },
            }}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Acceso rapido destacado ("Tu dia"/"Shessai IA") -- sin titulo de
          seccion, antes de cualquier categoria, nunca duplicado dentro
          de Atencion. */}
      {quickAccess.length > 0 && !filteredItems && (
        <Box sx={{ flexShrink: 0 }}>
          {quickAccess.map(item => (
            <NavItem key={item.to} item={item} drawerOpen={true} location={location} />
          ))}
          <Divider sx={{ mx: "8px", my: "6px", opacity: 0.12 }} />
        </Box>
      )}

      {/* Nav list */}
      <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", py: "2px", scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}>
        {filteredItems ? (
          <>
            {filteredItems.length === 0 ? (
              <Typography sx={{ color: (theme.palette.mode==="dark"?"rgba(255,255,255,0.25)":"rgba(0,0,0,0.25)"), fontSize: 11, textAlign: "center", py: 3 }}>Sin resultados</Typography>
            ) : filteredItems.map(item => (
              <NavItem key={item.to} item={item} drawerOpen={true} location={location} />
            ))}
          </>
        ) : (
          navGroups.filter(g => g.items.length > 0).map(group =>
            group.items.length === 1 ? (
              <NavItem key={group.items[0].to} item={group.items[0]} drawerOpen={true} location={location} />
            ) : (
              <NavGroup
                key={group.id}
                group={group}
                drawerOpen={true}
                isOpen={openGroups[group.id]}
                onToggle={() => toggleGroup(group.id)}
                location={location}
              />
            )
          )
        )}
      </Box>

      {/* Version */}
      {version && (
        <Typography sx={{ color: "rgba(255,255,255,0.40)", fontSize: 9, textAlign: "center", py: "4px", flexShrink: 0 }}>
          v{version}
        </Typography>
      )}

      {/* Avatar */}
      <AvatarFooter
        userName={userName}
        userInitials={userInitials}
        drawerOpen={true}
        onProfileClick={onProfileClick}
        userProfile={user.profile}
      />
    </Box>
  );
};

export default MainListItems;
